import { loadGanttProject } from '../gantt-chart/data/ganttDatabaseService';
import { GanttTask } from '../gantt-chart/data/types';

export interface CalendarGanttEvent {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  duration: number;
  progress: number;
  color: string;
  type: 'task' | 'group' | 'subtask';
  parentId?: string;
  description?: string;
}

export interface CalendarGanttDay {
  date: string;
  events: CalendarGanttEvent[];
  hiddenCount: number;
  hiddenEvents: CalendarGanttEvent[];
}

export interface ContinuousBar {
  task: CalendarGanttEvent;
  startDate: string;
  endDate: string;
  startDayIndex: number;
  endDayIndex: number;
  totalDays: number;
}

// Convert Gantt tasks to calendar events
const convertGanttTaskToCalendarEvent = (task: GanttTask): CalendarGanttEvent => {
  return {
    id: task.id,
    name: task.name,
    startDate: task.startDate,
    endDate: task.endDate,
    duration: task.duration,
    progress: task.progress,
    color: task.color || 'bg-blue-200',
    type: task.type,
    parentId: task.parentId,
    description: (task as any).description || ''
  };
};

// Get all Gantt tasks that span a specific date
const getTasksForDate = (tasks: GanttTask[], targetDate: string): CalendarGanttEvent[] => {
  const target = new Date(targetDate + 'T00:00:00');
  return tasks
    .filter(task => {
      const startDate = new Date(task.startDate + 'T00:00:00');
      const endDate = new Date(task.endDate + 'T00:00:00');
      return target >= startDate && target <= endDate;
    })
    .map(convertGanttTaskToCalendarEvent);
};

// Generate continuous bars for tasks spanning multiple days
const getContinuousBars = (tasks: GanttTask[], startDate: Date, endDate: Date): ContinuousBar[] => {
  const bars: ContinuousBar[] = [];
  
  tasks.forEach(task => {
    const taskStart = new Date(task.startDate + 'T00:00:00');
    const taskEnd = new Date(task.endDate + 'T00:00:00');
    
    // Check if task overlaps with the date range
    if (taskEnd >= startDate && taskStart <= endDate) {
      bars.push({
        task: convertGanttTaskToCalendarEvent(task),
        startDate: task.startDate,
        endDate: task.endDate,
        startDayIndex: 0, // Will be calculated in the calendar component
        endDayIndex: 0,   // Will be calculated in the calendar component
        totalDays: task.duration
      });
    }
  });
  
  return bars;
};

// Main function to get calendar Gantt data
export const getCalendarGanttData = async (
  projectId: string, 
  startDate: Date, 
  endDate: Date
): Promise<{ days: CalendarGanttDay[], continuousBars: ContinuousBar[] }> => {
  try {
    // Load Gantt project data
    const ganttProject = await loadGanttProject(projectId);
    
    if (!ganttProject || !ganttProject.tasks) {
      return { days: [], continuousBars: [] };
    }

    // Filter out groups and only get tasks for calendar display
    const tasks = ganttProject.tasks.filter(task => task.type === 'task');
    
    // Get all continuous bars for the entire date range
    const continuousBars = getContinuousBars(tasks, startDate, endDate);
    
    const days: CalendarGanttDay[] = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      const dateStr = current.toISOString().split('T')[0];
      const currentDate = new Date(dateStr + 'T00:00:00');
      
      // Find tasks that start on this specific date
      const dayTasks = continuousBars
        .filter(bar => {
          const taskStart = new Date(bar.startDate + 'T00:00:00');
          return taskStart.getTime() === currentDate.getTime();
        })
        .map(bar => bar.task);
      
      // Sort tasks by start date, then by name
      dayTasks.sort((a, b) => {
        const dateCompare = new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
        if (dateCompare !== 0) return dateCompare;
        return a.name.localeCompare(b.name);
      });
      
      const maxVisible = 3;
      const visibleTasks = dayTasks.slice(0, maxVisible);
      const hiddenTasks = dayTasks.slice(maxVisible);
      
      days.push({
        date: dateStr,
        events: visibleTasks,
        hiddenCount: hiddenTasks.length,
        hiddenEvents: hiddenTasks
      });
      
      current.setDate(current.getDate() + 1);
    }
    
    return { days, continuousBars };
  } catch (error) {
    console.error('Error loading calendar Gantt data:', error);
    return { days: [], continuousBars: [] };
  }
};

// Get calendar data for a specific day
export const getCalendarGanttDay = async (
  projectId: string, 
  date: Date
): Promise<CalendarGanttDay | null> => {
  const { days } = await getCalendarGanttData(projectId, date, date);
  return days.length > 0 ? days[0] : null;
};